// @flow

// Libs
import _filter from 'lodash/filter'
import _get from 'lodash/get'
import _map from 'lodash/map'
import _reverse from 'lodash/reverse'
import _head from 'lodash/head'
import _isEmpty from 'lodash/isEmpty'
import { createSelector } from 'reselect'

// Turf
import turfCenter from '@turf/center'
import turfArea from '@turf/area'
import { coordAll } from '@turf/meta'

// Utils
import convert from 'convert-units'
import emptyArrayOr from 'fc-utils/emptyArrayOr'

// Types
import type { Asset } from 'fc-model-assets/types'

// Selectors
import { fieldsOfSelectedFarmAndSeason$ } from 'hfty-assets'
import selectedJobAndGrid$ from 'hfty-soil-jobs/src/selectors/selectedJobAndGrid'
import operation$ from 'hfty-soil-jobs-dom/src/selectors/operation'
import featuresOperation$ from './featureOperation'

// Constants
import {
  PROP_ASSET_ID,
  PROP_SHAPE,
} from 'fc-model-assets'
import {
  PROP_SHAPE_DATA,
  PROP_FEATURES,
} from 'fc-model-shapes'
import { GRID_UNIT_ACRES } from 'hfty-model-grid'
import { EDIT } from 'fc-operations/src/constants'

// Export
export const fieldsToRenderOnMap$ = createSelector(
  [
    fieldsOfSelectedFarmAndSeason$,
    operation$,
    featuresOperation$,
    selectedJobAndGrid$,
  ],
  function (
    fields: Array<Asset>,
    currentOperation: Object,
    featuresOperation: Object,
    selectedJobAndGrid: Object,
  ): Array<Asset> {
    const fieldsWithShapeDataFeatures = _filter(fields, (field: Asset): boolean =>
      _get(field, `[${PROP_SHAPE}][${PROP_SHAPE_DATA}][${PROP_FEATURES}].length`) > 0)
    const selectedFeatureId = _get(featuresOperation, `model.[${PROP_ASSET_ID}]`)
    const isEdit = _get(featuresOperation, 'operation') === EDIT
    let filteredFeatures = fieldsWithShapeDataFeatures

    if (selectedFeatureId && isEdit) {
      filteredFeatures = _filter(fieldsWithShapeDataFeatures,
        (field: Asset): boolean => field[PROP_ASSET_ID] !== selectedFeatureId
      )
    }

    return emptyArrayOr(_map(
      filteredFeatures,
      (field: Asset): Asset => {
        const shape = _get(field, PROP_SHAPE)
        const shapeData = _get(field, `[${PROP_SHAPE}][${PROP_SHAPE_DATA}]`)
        const centroid = _reverse(_head(coordAll(turfCenter(shapeData))))
        const units = !_isEmpty(currentOperation)
          ? _get(currentOperation, 'model.gridData.grid.units', GRID_UNIT_ACRES)
          : _get(selectedJobAndGrid, 'grid.features[0].properties.units', GRID_UNIT_ACRES)
        const area = convert(turfArea(shapeData)).from('m2').to('ac').toFixed(2)

        return {
          ...field,
          [PROP_SHAPE]: {
            ...shape,
            [PROP_SHAPE_DATA]: {
              ...shapeData,
              centroid,
              area,
              units,
            },
          },
        }
      }
    ))
  }
)

export default fieldsToRenderOnMap$
